import { Action, configureStore, ThunkAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';

import appointmentsReducers, { appointmentApis } from '~appointments/reducers';
import careWorkersReducers from '~care-workers/reducers';
import clientsReducers from '~clients/reducers';
import dashboardReducers from '~dashboard/reducers';
import directDebitsReducers, { directDebitsApis } from '~direct-debits/reducers';
import lookupsReducers, { lookupApis } from '~lookups/reducers';
import mainReducers, { mainApis } from '~main/reducers';
import supportAtHomeReducers, { supportAtHomeApis } from '~support-at-home/reducers';
import roreReducers, { roreApis } from '~rore/reducers';
import weeklyPlannerReducers, { weeklyPlannerApis } from '~weekly-planner/reducers';

// Helpers - flatten and type APIs
type ApiLike = { reducerPath: string; reducer: any; middleware: any };

function collectApiReducers(apis: readonly ApiLike[]) {
  return Object.fromEntries(apis.map((api) => [api.reducerPath, api.reducer]));
}

function collectApiMiddleware(apis: readonly ApiLike[]) {
  return apis.map((api) => api.middleware);
}

const apiReducers = collectApiReducers([
  ...weeklyPlannerApis,
  ...mainApis,
  ...supportAtHomeApis,
  ...directDebitsApis,
  ...lookupApis,
  ...appointmentApis,
  ...roreApis,
]);
const apiMiddleware = collectApiMiddleware([
  ...weeklyPlannerApis,
  ...mainApis,
  ...supportAtHomeApis,
  ...directDebitsApis,
  ...lookupApis,
  ...appointmentApis,
  ...roreApis,
]);

export const store = configureStore({
  reducer: {
    appointments: appointmentsReducers,
    careWorkers: careWorkersReducers,
    clients: clientsReducers,
    dashboard: dashboardReducers,
    directDebits: directDebitsReducers,
    lookups: lookupsReducers,
    main: mainReducers,
    supportAtHome: supportAtHomeReducers,
    weeklyPlanner: weeklyPlannerReducers,
    rore: roreReducers,

    ...apiReducers,
  },
  middleware: (getDefault) => getDefault().concat(...apiMiddleware),
});

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
